import { useQuery } from '@tanstack/react-query';
import {
  getAccountDocumentDetails,
  getAccountDocuments,
} from '#/service/driver-management/endpoints/accountDocument.ts';
import { ACCOUNT_DOCUMENT_QUERY_NAMESPACE } from '#/service/driver-management/hooks/useAccountDocumentList.ts';
import { z } from 'zod';
import {
  AccountDocumentSummaryItemSchema,
  ApproveDrivingLicenseInfoSchema,
  ApproveVehicleRegistrationInfoSchema,
  type DocumentType,
} from '#/service/driver-management/model.ts';

const MAX_PAGE_SIZE = 200;

const getLatestAccountDocument = async (
  accountId: string,
  documentType: DocumentType,
) => {
  const { data } = await getAccountDocuments({
    accountId,
    documentType,
    pageSize: MAX_PAGE_SIZE,
  });
  return z.array(AccountDocumentSummaryItemSchema).parse(data).at(-1) ?? null;
};

const useLatestAccountDocument = (
  accountId: string,
  documentType: DocumentType,
) =>
  useQuery({
    queryKey: [
      ACCOUNT_DOCUMENT_QUERY_NAMESPACE,
      'latest',
      accountId,
      documentType,
    ],
    queryFn: () => getLatestAccountDocument(accountId, documentType),
  });

const detailQueryOptions = (documentId: string) => ({
  queryKey: [ACCOUNT_DOCUMENT_QUERY_NAMESPACE, 'detail', documentId],
  queryFn: () => getAccountDocumentDetails(documentId),
});

const useApprovedDrivingLicense = (documentId: string) =>
  useQuery({
    ...detailQueryOptions(documentId),
    select: (document) =>
      ApproveDrivingLicenseInfoSchema.parse(document.drivingLicenseInfo),
  });

const useApprovedVehicleRegistration = (documentId: string) =>
  useQuery({
    ...detailQueryOptions(documentId),
    select: (document) =>
      ApproveVehicleRegistrationInfoSchema.parse(
        document.vehicleRegistrationInfo,
      ),
  });

export {
  useApprovedDrivingLicense,
  useApprovedVehicleRegistration,
  useLatestAccountDocument,
};
